import * as THREE from 'three';
import { loadGlbMeshes } from './navmeshService';
import { MultisetRestClient } from './multisetRestClient';
import { logEvent } from './debugLog';
import { OCCLUDER_CULL_DIST_M, OCCLUDER_QUANT_CELL_M, occluderTriKey, quantizeToCell, triAreaSq, withinCullRadius } from '../utils/math';
import type { NavMeConfig } from '../types';

/**
 * Real-world occlusion: renders the MultiSet scan mesh invisibly (depth
 * only, no color) UNDER mapRoot, so AR content behind real walls, floors and
 * railings is hidden — no more breadcrumb rings shining through the banister.
 *
 * How it works: `colorWrite: false` writes the scan geometry to the depth
 * buffer but paints nothing; `renderOrder = -1` draws it before nav content,
 * so crumbs/pins behind real geometry fail the depth test and are culled by
 * the GPU.
 *
 * PERF (2026-07-12, user: "why is FPS low"): the scan arrives as ONE huge
 * mesh (759k tris here) — a single mesh can't be frustum-culled, so the GPU
 * chewed the whole venue every frame even when 80% of it was behind the
 * camera. The mesh is now split into ~5 m XZ chunks with individual bounding
 * spheres; three.js culls off-screen chunks automatically. Same visuals,
 * a fraction of the per-frame triangle load.
 */

/** Chunk edge length (metres) for frustum-culling buckets. */
const CHUNK_SIZE_M = 5;

let occluder: THREE.Group | null = null;
let enabled = true;
/** Memoize the PROMISE, not the result — attachOccluder fires on every localization, and memoizing the result alone let concurrent calls each build the 759k-tri group (seen 3× in debug logs). */
let loadPromise: Promise<THREE.Group | null> | null = null;

/**
 * #34 A/B (Test 13): decimation cut the occluder −69% with ZERO FPS change, so
 * geometry isn't the cost. This flag skips building/rendering the occluder
 * entirely to measure its true (fill-rate) cost — the next walk's `fpsAvg`
 * answers it: jumps ⇒ the depth pass was the cost (drop it for good); flat ⇒
 * the ceiling is the 8th Wall pipeline (occluder irrelevant). With it skipped,
 * `hasOccluder()` stays false so #32 keeps crumbs NEAR (few through-wall) and
 * the always-on arrow still guides.
 *
 * RESULT (Test 15): occluder OFF ⇒ FPS 38.7/20.4 = IDENTICAL to occluder ON
 * (38.2). The occluder is NOT the FPS cost at all — the ceiling is the 8th Wall
 * camera-mode pipeline. Flag restored to false: the occluder is free, and it
 * hides through-wall crumbs (quality win at no FPS cost).
 */
const SKIP_OCCLUDER_FOR_FPS_AB = false;

export function loadOccluder(config: NavMeConfig): Promise<THREE.Group | null> {
  if (SKIP_OCCLUDER_FOR_FPS_AB) {
    logEvent('navmesh', 'occluder SKIPPED (FPS A/B — measuring fill-rate cost; crumbs stay near via #32)');
    return Promise.resolve(null);
  }
  if (!loadPromise) loadPromise = doLoadOccluder(config);
  return loadPromise;
}

async function doLoadOccluder(config: NavMeConfig): Promise<THREE.Group | null> {
  try {
    const rest = new MultisetRestClient(config);
    const meshInfo = await rest.getMapMeshUrl();
    if (!meshInfo || meshInfo.format !== 'glb') return null;

    // Browser HTTP cache typically makes this cheap after the navmesh
    // generator already fetched the same file.
    const meshes = await loadGlbMeshes(meshInfo.url);
    if (meshes.length === 0) return null;

    const depthOnly = new THREE.MeshBasicMaterial({ colorWrite: false });
    const t0 = performance.now();
    const { group, triangles, chunks, originalTris } = buildChunkedOccluder(meshes, depthOnly);
    group.name = 'scan-occluder';
    group.visible = enabled;
    occluder = group;
    const pct = originalTris > 0 ? Math.round((1 - triangles / originalTris) * 100) : 0;
    logEvent(
      'navmesh',
      `occluder ready (${Math.round(triangles / 1000)}k tris in ${chunks} culling chunks, decimated from ${Math.round(originalTris / 1000)}k −${pct}% @${OCCLUDER_QUANT_CELL_M * 100}cm, ${Math.round(performance.now() - t0)}ms)`,
    );
    return group;
  } catch (err) {
    logEvent('error', `occluder load failed: ${err instanceof Error ? err.message : String(err)}`);
    loadPromise = null; // allow a retry on the next localization
    return null;
  }
}

/**
 * Rebuild the scan as per-cell depth meshes (world transform baked). Two
 * passes: count triangles per XZ cell, then fill pre-sized buffers — no
 * per-triangle array pushes (2.3M verts would crawl).
 */
function buildChunkedOccluder(
  meshes: THREE.Mesh[],
  mat: THREE.Material,
): { group: THREE.Group; triangles: number; chunks: number; originalTris: number } {
  interface Src {
    pos: THREE.BufferAttribute;
    index: THREE.BufferAttribute | null;
    matrix: THREE.Matrix4;
    triCount: number;
  }
  const srcs: Src[] = meshes.map((m) => {
    m.updateWorldMatrix(true, false);
    const pos = m.geometry.getAttribute('position') as THREE.BufferAttribute;
    const index = m.geometry.getIndex();
    return { pos, index, matrix: m.matrixWorld.clone(), triCount: (index ? index.count : pos.count) / 3 };
  });

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  // #34: grid-snap every vertex and drop triangles that collapse below a
  // grid-cell's worth of area — removes the scan's dense sub-cell noise. Solid
  // walls keep their large triangles, so occlusion is preserved (no holes).
  // Applied identically in BOTH passes so the pre-sized buffer counts match.
  const Q = OCCLUDER_QUANT_CELL_M;
  const DEGEN_AREA_SQ = ((Q * Q) / 4) ** 2;
  const forEachTri = (
    count: ((cell: string) => void) | null,
    fill?: Map<string, { arr: Float32Array; off: number }>,
  ) => {
    // Post-snap DEDUP (#34, Test 10): after grid-snapping, a scan's redundant
    // coplanar wall triangles collapse to identical vertex triples — remove the
    // duplicates. Rebuilt identically each pass (same iteration order) so the
    // pre-sized count/fill buffers stay in lockstep.
    const seen = new Set<string>();
    for (const s of srcs) {
      for (let t = 0; t < s.triCount; t++) {
        const i0 = s.index ? s.index.getX(t * 3) : t * 3;
        const i1 = s.index ? s.index.getX(t * 3 + 1) : t * 3 + 1;
        const i2 = s.index ? s.index.getX(t * 3 + 2) : t * 3 + 2;
        a.fromBufferAttribute(s.pos, i0).applyMatrix4(s.matrix);
        b.fromBufferAttribute(s.pos, i1).applyMatrix4(s.matrix);
        c.fromBufferAttribute(s.pos, i2).applyMatrix4(s.matrix);
        a.set(quantizeToCell(a.x, Q), quantizeToCell(a.y, Q), quantizeToCell(a.z, Q));
        b.set(quantizeToCell(b.x, Q), quantizeToCell(b.y, Q), quantizeToCell(b.z, Q));
        c.set(quantizeToCell(c.x, Q), quantizeToCell(c.y, Q), quantizeToCell(c.z, Q));
        if (triAreaSq(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z) < DEGEN_AREA_SQ) continue;
        const key = occluderTriKey(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
        if (seen.has(key)) continue;
        seen.add(key);
        const cell = `${Math.floor((a.x + b.x + c.x) / 3 / CHUNK_SIZE_M)}:${Math.floor((a.z + b.z + c.z) / 3 / CHUNK_SIZE_M)}`;
        if (fill) {
          const buf = fill.get(cell)!;
          const o = buf.off;
          buf.arr[o] = a.x; buf.arr[o + 1] = a.y; buf.arr[o + 2] = a.z;
          buf.arr[o + 3] = b.x; buf.arr[o + 4] = b.y; buf.arr[o + 5] = b.z;
          buf.arr[o + 6] = c.x; buf.arr[o + 7] = c.y; buf.arr[o + 8] = c.z;
          buf.off += 9;
        } else {
          count?.(cell);
        }
      }
    }
  };

  const counts = new Map<string, number>();
  forEachTri((cell) => counts.set(cell, (counts.get(cell) ?? 0) + 1));
  const buffers = new Map<string, { arr: Float32Array; off: number }>();
  for (const [cell, n] of counts) buffers.set(cell, { arr: new Float32Array(n * 9), off: 0 });
  forEachTri(null, buffers);

  const group = new THREE.Group();
  let triangles = 0;
  for (const { arr } of buffers.values()) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    g.computeBoundingSphere(); // per-chunk sphere = automatic frustum culling
    const mesh = new THREE.Mesh(g, mat);
    mesh.renderOrder = -1;
    group.add(mesh);
    triangles += arr.length / 9;
  }
  const originalTris = srcs.reduce((n, s) => n + s.triCount, 0);
  return { group, triangles, chunks: group.children.length, originalTris };
}

export function setOccluderEnabled(on: boolean): void {
  enabled = on;
  if (occluder) occluder.visible = on;
}

export function isOccluderEnabled(): boolean {
  return enabled;
}

/** True once the depth mesh is actually built and in the scene. */
export function hasOccluder(): boolean {
  return occluder !== null;
}

/**
 * Distance culling on top of frustum culling (perf, 2026-07-12 "FPS was
 * higher before features"; tightened #31 Test 8): chunks beyond
 * OCCLUDER_CULL_DIST_M (utils/math) are hidden entirely — far occlusion is
 * invisible (AR content only exists within the ~11 m crumb reveal window), and
 * the old 25 m budget culled nothing in a house, so the full 759k-tri scan
 * drew every frame and pulled FPS to 29–30 on open sightlines.
 */

/** Call ~2 Hz with the user's MAP-space position (chunks live under mapRoot). */
export function updateOccluderLOD(userMapPos: { x: number; z: number }): void {
  if (!occluder || !enabled) return;
  for (const child of occluder.children) {
    const mesh = child as THREE.Mesh;
    const s = (mesh.geometry as THREE.BufferGeometry).boundingSphere;
    if (!s) continue;
    const dx = s.center.x - userMapPos.x;
    const dz = s.center.z - userMapPos.z;
    mesh.visible = withinCullRadius(dx * dx + dz * dz, s.radius, OCCLUDER_CULL_DIST_M);
  }
}
